import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { updateWorkspaceName } from "../../api/workspaceSettings";
import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import { useCurrentWorkspaceStore } from "../../store/useCurrentWorkspaceStore";
import useWorkspaceInformationStore from "../../store/useWorkspaceInformationStore";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import useUserStore from "@/features/auth/store/useUserStore";

interface EditableWorkspaceNameProps {
  workspaceName: string;
  workspaceType: "personal" | "group";
  className?: string;
}

export const EditableWorkspaceName = ({
  workspaceName,
  workspaceType,
  className,
}: EditableWorkspaceNameProps) => {
  const { t } = useTranslation(["workspaces"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { setSettings, refetch } = useWorkspaceSettingsStore();
  const { updateWorkspaceName: updateCurrentWorkspaceName } = useCurrentWorkspaceStore();
  const { updateWorkspaceName: updateWorkspaceInfoName } = useWorkspaceInformationStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUserStore();

  // Schema with translated error messages
  const workspaceNameSchema = z.object({
    name: z
      .string()
      .min(1, t("settings.workspaceInfo.nameRequired"))
      .max(100, t("settings.workspaceInfo.nameMaxLength"))
      .regex(/^[a-zA-Z0-9\s\-_.,!?()]+$/, t("settings.workspaceInfo.nameInvalidChars")),
  });

  type WorkspaceNameFormData = z.infer<typeof workspaceNameSchema>;

  const form = useForm<WorkspaceNameFormData>({
    resolver: zodResolver(workspaceNameSchema),
    defaultValues: {
      name: workspaceName,
    },
  });

  const handleEditClick = () => {
    if (workspaceType === "personal" || user?.role === "student") {
      toast.error(t("settings.workspaceInfo.cannotChangePersonalName"));

      return;
    }
    setIsEditing(true);
    form.setValue("name", workspaceName);
    // Focus the input after the animation completes
    setTimeout(() => {
      const input = document.querySelector("[data-workspace-name-input]") as HTMLInputElement;

      if (input) {
        input.focus();
        input.select();
      }
    }, 150);
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.reset({ name: workspaceName });
  };

  const handleSubmit = async (data: WorkspaceNameFormData) => {
    if (!workspaceId) return;

    setIsSubmitting(true);
    try {
      const response = await updateWorkspaceName(workspaceId, data);

      // Update the store with the new name
      const { settings } = useWorkspaceSettingsStore.getState();
      const { currentWorkspace } = useCurrentWorkspaceStore.getState();
      const { data: workspaceInfo } = useWorkspaceInformationStore.getState();

      if (settings) {
        setSettings({
          ...settings,
          workspace: {
            ...settings.workspace,
            name: response.data.name,
          },
        });
      }

      // Update current workspace store
      if (currentWorkspace) {
        updateCurrentWorkspaceName(response.data.name);
      }

      // Update workspace information store
      if (workspaceInfo) {
        updateWorkspaceInfoName(response.data.name);
      }

      toast.success(t("settings.workspaceInfo.nameUpdated"));

      // Refetch settings to ensure data consistency
      if (workspaceId) {
        await refetch(workspaceId);
      }

      setIsEditing(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || t("settings.workspaceInfo.nameUpdateFailed");

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canEdit = workspaceType === "group" && user?.role === "tutor";

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <AnimatePresence mode="wait">
        {!isEditing ? (
          <motion.div
            key="display"
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-1 items-center gap-2"
            exit={{ opacity: 0, scale: 0.95 }}
            initial={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          >
            <button
              className={cn(
                "text-accent-foreground flex-1 text-left text-lg font-semibold md:text-xl",
                canEdit && "hover:text-accent-foreground/80 cursor-pointer transition-colors",
                !canEdit && "cursor-default",
              )}
              disabled={!canEdit}
              onClick={handleEditClick}
            >
              {workspaceName}
            </button>

            {canEdit && (
              <Button
                className="hover:bg-muted/30 h-6 w-6 p-0"
                size="sm"
                title={t("workspaces:settings.workspaceInfo.editName")}
                variant="ghost"
                onClick={handleEditClick}
              >
                <Icon className="text-muted-foreground h-3 w-3" icon="lucide:edit-2" />
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.form
            key="editing"
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-1 flex-col"
            exit={{ opacity: 0, scale: 0.95 }}
            initial={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <div className={"flex flex-1 items-center gap-2"}>
              <div className="relative flex-1">
                <Input
                  {...form.register("name")}
                  data-workspace-name-input
                  className={cn(
                    "bg-muted/30 border-border/50 text-lg font-semibold md:text-xl",
                    form.formState.errors.name && "border-red-500",
                  )}
                  disabled={isSubmitting}
                  placeholder={workspaceName}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      handleCancel();
                    }
                  }}
                />
              </div>

              <div className="flex items-center gap-1">
                <Button
                  className="h-6 w-6 p-0 hover:bg-green-500/20"
                  disabled={isSubmitting || !form.formState.isValid}
                  size="sm"
                  title={t("workspaces:settings.workspaceInfo.saveName")}
                  type="submit"
                  variant="ghost"
                >
                  {isSubmitting ? (
                    <Icon className="h-3 w-3 animate-spin text-green-600" icon="lucide:loader-2" />
                  ) : (
                    <Icon className="h-3 w-3 text-green-600" icon="lucide:check" />
                  )}
                </Button>

                <Button
                  className="h-6 w-6 p-0 hover:bg-red-500/20"
                  disabled={isSubmitting}
                  size="sm"
                  title={t("workspaces:settings.workspaceInfo.cancelEdit")}
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                >
                  <Icon className="h-3 w-3 text-red-600" icon="lucide:x" />
                </Button>
              </div>
            </div>
            {form.formState.errors.name && (
              <motion.p
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-start text-xs text-red-500"
                initial={{ opacity: 0, y: -5 }}
              >
                {form.formState.errors.name.message}
              </motion.p>
            )}
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
